import {useCallback, useEffect, useMemo} from 'react';

import {batch, useDispatch} from 'react-redux';

import {showLoading, updateLoading} from '@/actions/loadingActions';
import * as modalActions from '@/actions/modalActions';
import {
  selectedFileExport,
  setDoubleTopMenuProperties,
  setTopMenuProperties,
  topButtonTertiaryProperties,
  usbOpenFor,
  usbProperties,
} from '@/actions/setupActions';
import {GetRequest} from '@/api/request';
import DownloadIcon from '@/assets/icons/Download.svg';
import {GET_CYCLE_HISTORY_DETAIL} from '@/constants/apis';
import {PROFILE_HISTORY} from '@/constants/messages';
import {
  CYCLE_HISTORY,
  EXPORT,
  SYSTEM_LOGS_ADDRESS,
  OPEN,
  USB_FOLDER_LIST_ADDRESS,
  USB_OPEN_FOR,
} from '@/constants/pcConstAppData';
import {useCycleHistoryRemoteViewContext} from '@/contexts/CycleHistoryContext';
import {useCycleHistoryChangeDetailContext, useCycleHistoryDetailContext} from '@/contexts/CycleHistoryDetailContext';
import UseUsb from '@/hooks/useUsb';
import {consoleLog} from '@/libs/consoleHelper';
import {DateToHMS, DateToString, ParseToDate} from '@/libs/dateHelper';

import {Download} from './business/RemoteCycleHistory';

// TODO need to refactor into pure component, currently caused by the generated type of component it make difficult to control the flow behavior
function CycleHistoryPageTopMenu() {
  const dispatch = useDispatch();
  const {usbList, RefreshUsbList} = UseUsb();
  const {isRemoteView} = useCycleHistoryRemoteViewContext();
  const {selectedCycleHistoryList, isShowDetail, SetSelectedCycleHistoryList} = useCycleHistoryDetailContext();
  const SetCycleLog = useCycleHistoryChangeDetailContext();

  const openedCycleHistory = useMemo(() => selectedCycleHistoryList[0], [selectedCycleHistoryList]);
  const disableExportButton = useMemo(() => selectedCycleHistoryList.length === 0, [selectedCycleHistoryList]);
  const disableOpenButton = useMemo(() => selectedCycleHistoryList.length !== 1, [selectedCycleHistoryList]);

  useEffect(() => {
    if (usbList.length > 0) {
      batch(() => {
        dispatch(usbOpenFor(USB_OPEN_FOR.EXPORT_CYCLE_HYSTORY));
        dispatch(usbProperties(usbList));
        dispatch(selectedFileExport(selectedCycleHistoryList));
      });
      window.location.href = USB_FOLDER_LIST_ADDRESS;
    }
  }, [usbList, selectedCycleHistoryList]);

  const OnBackActionHandler = useCallback(() => {
    if (isShowDetail) {
      SetCycleLog(null);
      SetSelectedCycleHistoryList(openedCycleHistory);
    } else window.location.href = SYSTEM_LOGS_ADDRESS;
  }, [SetCycleLog, SetSelectedCycleHistoryList, isShowDetail, openedCycleHistory]);

  const OnOpenActionHandler = useCallback(() => {
    const selectedLog = openedCycleHistory;
    const params = {
      params: {
        path: selectedLog.Path,
      },
    };
    dispatch(
      updateLoading({
        showLoading: true,
        loadingText: PROFILE_HISTORY.OPEN_DETAIL_LOADING,
      }),
    );
    GetCycleLogDetails(params)
      .then((response) => {
        const {cycleData} = response.data;
        SetCycleLog(cycleData);
      })
      .finally(() => dispatch(showLoading(false)));
  }, [openedCycleHistory]);

  const promptConfirmation = () => {
    dispatch(
      modalActions.showPopUp({
        popUpTitle: 'Export Multiple Files',
        popUpText: `You are about to export ${selectedCycleHistoryList.length} files.`,
        popUpQuestion: 'Are you sure you want to continue?',
        popUpButton: [
          {
            id: 'YES',
            isPrimaryButton: true,
            onClick: () => {
              dispatch(modalActions.hidePopUp());
              dispatch(RefreshUsbList);
            },
            label: 'YES',
          },
          {
            id: 'CANCEL',
            isPrimaryButton: false,
            onClick: () => {
              dispatch(modalActions.hidePopUp());
            },
            label: 'CANCEL',
          },
        ],
      }),
    );
  };

  const OnDownloadActionHandler = useCallback(() => {
    dispatch(
      updateLoading({
        showLoading: true,
        loadingText: PROFILE_HISTORY.OPEN_DETAIL_LOADING,
      }),
    );
    selectedCycleHistoryList.forEach((selectedLog, index) => {
      const params = {
        params: {
          path: selectedLog.Path,
        },
      };
      GetCycleLogDetails(params)
        .then((response) => {
          const {fileName, cycleData} = response.data;
          const csvString = [
            [
              'Date',
              'Time',
              'Cycle',
              'State',
              'SampleVolume',
              'BufferVolume',
              'Concentration',
              'VacuumPressure',
              'ValvePressure',
              'DiaphragmPressure',
              'ReturnValvePressure',
              'ElapsedTime',
              'Notes',
            ],
            ...cycleData.map((item) => {
              const itemTimeStamp = ParseToDate(item.Time);
              return [
                DateToString(itemTimeStamp),
                DateToHMS(itemTimeStamp),
                item.Cycle,
                item.State,
                item.SampleVolume,
                item.BufferVolume,
                item.Concentration,
                item.VacuumPressure,
                item.ValvePressure,
                item.DiaphragmPressure,
                item.ReturnValvePressure,
                item.ElapsedTime,
                item.Notes,
              ];
            }),
          ]
            .map((e) => e.join(';'))
            .join('\n');
          Download(fileName, csvString);
        })
        .finally(() => {
          if (index === selectedCycleHistoryList.length - 1) dispatch(showLoading(false));
        });
    });
  }, [selectedCycleHistoryList]);

  const GetCycleLogDetails = (params) => GetRequest(GET_CYCLE_HISTORY_DETAIL, params);

  useEffect(() => {
    if (!isShowDetail) {
      consoleLog('Top Menu useEffect', {selectedCycleHistoryList, disableExportButton, disableOpenButton});
      const topMenuTitle = CYCLE_HISTORY;
      const backButtonProperties = {
        id: 'btn-back-cycle-log',
        onClick: OnBackActionHandler,
        disabled: false,
      };
      const exportButtonProperties = {
        id: 'btn-export-cycle-log',
        label: EXPORT,
        onClick: disableOpenButton ? promptConfirmation : RefreshUsbList,
        disabled: disableExportButton,
        className: 'infoButton',
      };
      const downloadButtonProperties = {
        id: 'btn-download-cycle-log',
        label: 'DOWNLOAD',
        icon: DownloadIcon,
        onClick: OnDownloadActionHandler,
        disabled: disableExportButton,
      };
      const openButtonProperties = {
        id: 'btn-open-cycle-log',
        label: OPEN,
        onClick: OnOpenActionHandler,
        disabled: disableOpenButton,
        className: 'save-button',
      };
      const topButtonProperties = isRemoteView ? downloadButtonProperties : exportButtonProperties;
      const backButton = isRemoteView ? undefined : backButtonProperties;
      DoubleTopMenuSetup(topButtonProperties, openButtonProperties, backButton, topMenuTitle);
      // consoleLog("Cycle History Top Menu Double", {
      //   isShowDetail,
      //   selectedCycleHistoryList,
      //   disableExportButton,
      //   disableOpenButton,
      //   topButtonProperties,
      //   backButtonProperties,
      //   topMenuTitle,
      // });
    }
  }, [
    selectedCycleHistoryList,
    disableExportButton,
    disableOpenButton,
    OnBackActionHandler,
    RefreshUsbList,
    OnOpenActionHandler,
  ]);

  useEffect(() => {
    if (isShowDetail) {
      const exportButtonProperties = {
        id: 'btn-export-cycle-log',
        label: EXPORT,
        onClick: disableOpenButton ? promptConfirmation : RefreshUsbList,
        disabled: false,
      };
      const downloadButtonProperties = {
        id: 'btn-download-cycle-log',
        label: 'DOWNLOAD',
        onClick: OnDownloadActionHandler,
        disabled: false,
      };
      const backButtonProperties = {
        id: 'btn-back-cycle-log',
        label: '',
        disabled: false,
        onClick: OnBackActionHandler,
      };
      const profileHistoryName = openedCycleHistory.Name.split(' - ');
      const titleMenuProperties =
        profileHistoryName.length > 1
          ? profileHistoryName[profileHistoryName.length - 1].toUpperCase()
          : openedCycleHistory.Name.toUpperCase();
      const topButtonProperties = isRemoteView ? downloadButtonProperties : exportButtonProperties;
      TopMenuSetup(topButtonProperties, backButtonProperties, titleMenuProperties);
      // consoleLog("Cycle History Top Menu", { isShowDetail, openedCycleHistory, isRemoteView, topButtonProperties, backButtonProperties, titleMenuProperties });
    }
  }, [isShowDetail, openedCycleHistory, isRemoteView, OnBackActionHandler]);

  const TopMenuSetup = (topButtonProperties, backButtonProperties, titleMenuProperties, secondaryButtonProps) => {
    batch(() => {
      dispatch(
        setTopMenuProperties({
          topButtonProperties:
            topButtonProperties === undefined
              ? undefined
              : {
                  id: topButtonProperties.id,
                  label: topButtonProperties.label,
                  disabled: topButtonProperties.disabled,
                  onClick: topButtonProperties.onClick,
                },
          backButtonProperties:
            backButtonProperties === undefined
              ? undefined
              : {
                  id: backButtonProperties.id,
                  label: backButtonProperties.label,
                  disabled: backButtonProperties.disabled,
                  onClick: backButtonProperties.onClick,
                },
          titleMenuProperties,
          topButtonSecondaryButtonProperties: secondaryButtonProps,
        }),
      );
      dispatch(topButtonTertiaryProperties(undefined));
    });
  };

  const DoubleTopMenuSetup = (button1Properties, button2Properties, backButtonProperties, titleMenuProperties) => {
    batch(() => {
      dispatch(
        setDoubleTopMenuProperties({
          button1Properties:
            button1Properties === undefined
              ? undefined
              : {
                  id: button1Properties.id,
                  label: button1Properties.label,
                  onClick: button1Properties.onClick,
                  disabled: button1Properties.disabled,
                  className: button1Properties.className,
                },
          button2Properties:
            button2Properties === undefined
              ? undefined
              : {
                  id: button2Properties.id,
                  label: button2Properties.label,
                  onClick: button2Properties.onClick,
                  disabled: button2Properties.disabled,
                  className: button2Properties.className,
                },
          backButtonProperties:
            backButtonProperties === undefined
              ? undefined
              : {
                  id: backButtonProperties.id,
                  label: '',
                  disabled: backButtonProperties.disabled,
                  onClick: backButtonProperties.onClick,
                },
          titleMenuProperties,
        }),
      );
      dispatch(topButtonTertiaryProperties(undefined));
    });
  };

  return null;
}
export default CycleHistoryPageTopMenu;
